import { useState, useCallback, useEffect, RefObject } from 'react';
import { Company, CostCenter, User } from '../types';
import { saveSetting, saveRoles, saveRoleColors } from '../services/rhDataService';
import {
  normalizeRolesList,
  normalizeCostCentersList,
  normalizeRoleColorsMap,
} from '../utils/textFormat';
import { BOARD_INITIAL } from './appDefaults';

export interface UseBoardSettingsParams {
  isAuthenticated: boolean;
  currentUser: User | null;
  currentCompany: Company | null;
  currentCompanyId: string | null;
  isDataLoadedRef: RefObject<boolean>;
  getSB: () => ReturnType<typeof import('../services/postgresClient').getPostgresClient>;
}

/**
 * Configurações por empresa: colunas, roles, cores, centros de custo e datas de afastamento.
 * Persiste automaticamente quando o usuário tem perm_settings.
 * @param params - Contexto de auth e acesso ao Postgres
 * @returns Estado de settings e helpers de persistência
 */
export function useBoardSettings({
  isAuthenticated,
  currentUser,
  currentCompany,
  currentCompanyId,
  isDataLoadedRef,
  getSB,
}: UseBoardSettingsParams) {
  const [columns, setColumns] = useState<{ id: string; title: string; color?: string }[]>(
    BOARD_INITIAL.columns,
  );
  const [hiringLimits, setHiringLimits] = useState<Record<string, number>>({});
  const [availableRoles, setAvailableRoles] = useState<string[]>([]);
  const [roleColors, setRoleColors] = useState<Record<string, string>>({});
  const [afastamentoDates, setAfastamentoDates] = useState<Record<string, string>>({});
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);

  const hasSettingsPermission = Boolean(currentUser?.permissions.settings);

  const canPersistSettings = useCallback(async () => {
    if (!isAuthenticated || !currentUser || !currentUser.permissions.settings) {
      return { ok: false as const, sb: null as ReturnType<typeof getSB> | null };
    }
    const sb = getSB();
    const { data: { session } } = await sb.auth.getSession();
    if (!session?.user) return { ok: false as const, sb };
    return { ok: true as const, sb };
  }, [isAuthenticated, currentUser, getSB]);

  const persistSettingIfAuthenticated = useCallback(
    async (key: string, value: unknown, companyId?: string) => {
      try {
        const { ok, sb } = await canPersistSettings();
        if (!ok || !sb) return;
        await saveSetting(sb, key, value, companyId);
      } catch (err) {
        console.warn(`[settings] Falha ao persistir "${key}"`, err);
      }
    },
    [canPersistSettings],
  );

  const resetSettingsState = useCallback(() => {
    setColumns(BOARD_INITIAL.columns);
    setHiringLimits({});
    setAvailableRoles([]);
    setRoleColors({});
    setAfastamentoDates({});
    setCostCenters([]);
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !currentCompany || !isDataLoadedRef.current) return;
    let changed = false;
    const normalizedRoles = normalizeRolesList(availableRoles);
    if (
      normalizedRoles.length !== availableRoles.length ||
      normalizedRoles.some((r, i) => r !== availableRoles[i])
    ) {
      changed = true;
    }
    const normalizedColors = normalizeRoleColorsMap(roleColors);
    if (JSON.stringify(normalizedColors) !== JSON.stringify(roleColors)) changed = true;
    const normalizedCC = normalizeCostCentersList(costCenters);
    if (JSON.stringify(normalizedCC) !== JSON.stringify(costCenters)) changed = true;
    if (changed) {
      setAvailableRoles(normalizedRoles);
      setRoleColors(normalizedColors);
      setCostCenters(normalizedCC);
    }
  }, [isAuthenticated, currentCompany, availableRoles, costCenters, roleColors, isDataLoadedRef]);

  useEffect(() => {
    if (
      !hasSettingsPermission ||
      !isDataLoadedRef.current ||
      !isAuthenticated ||
      !currentCompany ||
      !currentCompanyId
    ) {
      return;
    }
    const sb = getSB();
    saveRoles(sb, currentCompanyId, availableRoles).catch((err) =>
      console.warn('[settings] Falha ao salvar roles', err),
    );
  }, [availableRoles, hasSettingsPermission]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      !hasSettingsPermission ||
      !isDataLoadedRef.current ||
      !isAuthenticated ||
      !currentCompany ||
      !currentCompanyId
    ) {
      return;
    }
    const sb = getSB();
    saveRoleColors(sb, currentCompanyId, roleColors).catch((err) =>
      console.warn('[settings] Falha ao salvar role colors', err),
    );
  }, [roleColors, hasSettingsPermission]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!hasSettingsPermission || !isDataLoadedRef.current || !isAuthenticated || !currentCompany) return;
    void persistSettingIfAuthenticated('cost_centers', costCenters);
  }, [costCenters, persistSettingIfAuthenticated, isAuthenticated, currentCompany, hasSettingsPermission]);

  return {
    columns,
    setColumns,
    hiringLimits,
    setHiringLimits,
    availableRoles,
    setAvailableRoles,
    roleColors,
    setRoleColors,
    afastamentoDates,
    setAfastamentoDates,
    costCenters,
    setCostCenters,
    hasSettingsPermission,
    canPersistSettings,
    persistSettingIfAuthenticated,
    resetSettingsState,
  };
}
